import type { FC } from 'react';
import { Box, Flex, Spinner, useBreakpointValue } from '@chakra-ui/react';
import parse from 'html-react-parser';
import { FormattedMessage, useIntl } from 'react-intl';
import { useBasket } from '../../../../bff-api/baskets/useBasket';
import { useDeleteSaveForLater } from '../../../../bff-api/customers/useDeleteSaveForLater';
import { useMoveSaveForLaterToBasket } from '../../../../bff-api/customers/useMoveSaveForLaterToBasket';
import { RecommendationLocation } from '../../../../components/recommendations/RecommendationLocation';
import { ResponsiveImage } from '../../../../components/responsive-image';
import { SoftLink } from '../../../../components/soft-link/SoftLink';
import { useGTM } from '../../../../hooks';
import { GTMAddToBagEvent } from '../../../../hooks/gtm/model/events/GTMAddToBagEvent';
import { useSLTToast } from '../../../../hooks/use-toast/useSLTToast';
import { Button, Text } from '../../../../ui-components';
import { IconButton } from '../../../../ui-components/button/icon-button';
import { ProductPrice } from '../../../../ui-components/product-card/ProductPrice';
import { buildPDPUrl } from '../../../../utils/product-utils';
import { useCartState } from '../../contexts/useCartState';
import type { Product } from '../../../../bff-api/products/model';

export interface Props {
    product: Product;
    isDisabled: boolean;
}

export const SaveForLaterProduct: FC<Props> = ({ product, isDisabled }) => {
    const intl = useIntl();
    const showToast = useSLTToast();
    const widthHeight = useBreakpointValue({ xs: '164px', sm: '200px' });
    const moveSaveForLaterToBasket = useMoveSaveForLaterToBasket();
    const deleteSaveForLater = useDeleteSaveForLater();
    const { itemRefs } = useCartState();
    const { onAddToBag } = useGTM();
    const { data: basket } = useBasket();

    const { id, name, c_imageUrl } = product;
    const productName = parse(name!) as string;
    const isOutOfStock = !product.inventory?.orderable;

    const onAdd = async (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>): Promise<void> => {
        evt.preventDefault();
        evt.stopPropagation();

        await moveSaveForLaterToBasket.mutateAsync(id);

        onAddToBag(new GTMAddToBagEvent('SAVE_FOR_LATER', basket!, product));

        showToast(
            intl.formatMessage({
                id: 'product.has.been.added.back.to.bag',
                defaultMessage: 'Item added back to bag',
            }),
        );

        setTimeout(() => {
            const itemRef = itemRefs.current.get(id);
            const headerRef = itemRefs.current.get('cart-header');

            if (itemRef) {
                const top =
                    window.scrollY +
                    (itemRef.getBoundingClientRect()?.top ?? 0) -
                    (headerRef?.getBoundingClientRect()?.height ?? 0) -
                    20;

                window.scrollTo({ top, behavior: 'smooth' });
            }
        }, 300);
    };
    const onDelete = async (
        evt: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ): Promise<void> => {
        evt.preventDefault();
        evt.stopPropagation();

        await deleteSaveForLater.mutateAsync(id);
    };

    return (
        <SoftLink data-testid={`save-for-later-product-${id}`} to={buildPDPUrl(product)}>
            <Flex
                direction="column"
                flexShrink={0}
                width={{
                    base: 164,
                    sm: 200,
                }}
                gap={2}
                height={{ base: 406, sm: 442 }}
            >
                {c_imageUrl ? (
                    <ResponsiveImage
                        id={`save-for-later-product-${id}-img`}
                        src={c_imageUrl}
                        alt={productName}
                        width={widthHeight}
                        height={widthHeight}
                        className="mt-px aspect-square rounded-t-[8px]"
                        loading="lazy"
                    />
                ) : null}

                <Flex direction="column" justifyContent="space-between" height="100%">
                    <Flex direction="column" gap={4}>
                        <Box display="flex" flexGrow="1" h="48px" mb={{ base: 4, md: 6 }}>
                            <Text id={`save-for-later-product-${id}-name`} noOfLines={2}>
                                {productName}
                            </Text>
                        </Box>

                        <ProductPrice
                            location={RecommendationLocation.SAVE_FOR_LATER}
                            product={product}
                        />

                        {isOutOfStock ? (
                            <Text
                                id={`save-for-later-product-${id}-out-of-stock`}
                                color="slt-red-error"
                                lineHeight="1rem"
                                margin="7px 0 0"
                            >
                                <FormattedMessage id="out.of.stock" defaultMessage="Out of Stock" />
                            </Text>
                        ) : null}
                    </Flex>

                    <Flex justifyContent="space-between">
                        <Flex gap={1} alignItems="center">
                            <Button
                                id={`save-for-later-product-${id}-remove-button`}
                                variant="text-button-no-hover"
                                textSize="base"
                                onClick={onDelete}
                                isDisabled={deleteSaveForLater.isLoading || isDisabled}
                            >
                                <FormattedMessage id="remove" defaultMessage="Remove" />
                            </Button>

                            {deleteSaveForLater.isLoading ? (
                                <Spinner
                                    id={`save-for-later-product-${id}-remove-button-loading`}
                                    size="xs"
                                    color="slt-pepper.1000"
                                />
                            ) : null}
                        </Flex>

                        <IconButton
                            id={`save-for-later-product-${id}-add-to-bag-button`}
                            iconName="add-to-bag"
                            size="lg"
                            onClick={onAdd}
                            isDisabled={isDisabled || isOutOfStock}
                            isLoading={moveSaveForLaterToBasket.isLoading}
                        />
                    </Flex>
                </Flex>
            </Flex>
        </SoftLink>
    );
};
